/**
 * Realtor Referrals page (Phase 6).
 *
 * Read-only V1 surface: lists the realtor's referrals with status pills
 * and a "credit pending" treatment. Includes a "Download CSV" button
 * that hits the backend export endpoint and triggers a browser
 * download.
 *
 * Referrals are seeded server-side by the buyer-handoff claim flow
 * and walked by project-create / hire hooks; this page is purely
 * surface — no mutation actions in V1.
 */

import {
	CheckCircle2,
	Clock,
	Download,
	HandCoins,
	Loader2,
	RefreshCw,
	Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	downloadCrmCsv,
	listMyReferrals,
	type ReferralRow,
	type ReferralStatus,
} from "@/features/realtor/services/realtor-referrals-api.service";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; rows: ReferralRow[] };

export function RealtorReferralsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [exporting, setExporting] = useState(false);
	const unmountedRef = useRef(false);

	const runLoad = useCallback(async () => {
		setLoad({ status: "loading" });
		const result = await listMyReferrals();
		if (unmountedRef.current) return;
		if (!result.success || !result.data) {
			setLoad({
				status: "error",
				message: result.error ?? t("realtor.referrals.loadFailedFallback"),
			});
			return;
		}
		setLoad({ status: "loaded", rows: result.data });
	}, [t]);

	useEffect(() => {
		unmountedRef.current = false;
		runLoad();
		return () => {
			unmountedRef.current = true;
		};
	}, [runLoad]);

	const handleExport = async () => {
		setExporting(true);
		try {
			const result = await downloadCrmCsv();
			// The download anchor click already fired inside the
			// service if the request succeeded — only the visible page
			// state needs guarding to avoid setState/toast on an
			// unmounted page.
			if (unmountedRef.current) return;
			if (!result.success) {
				toast.error(result.error ?? t("realtor.referrals.exportFailed"));
				return;
			}
			toast.success(t("realtor.referrals.exportSuccess"));
		} finally {
			// Belt-and-braces: if downloadCrmCsv ever changes from
			// "returns ApiResult" to "throws", the button shouldn't
			// stay stuck in the busy state.
			if (!unmountedRef.current) setExporting(false);
		}
	};

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-6"
				data-testid="realtor-referrals-loading"
			>
				<Skeleton className="h-12 w-full" />
				<Skeleton className="h-32 w-full" />
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card
					className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]"
					style={{
						borderColor: "var(--color-dashboard-pill-risk)",
					}}
				>
					<CardContent className="p-5 space-y-3" role="alert">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<StudioButton
							variant="secondary"
							className="gap-1.5"
							onClick={runLoad}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.referrals.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { rows } = load;

	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-6">
			<header className="flex items-start justify-between gap-3">
				<div>
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						Realtor Portal
					</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 32,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
					>
						<span style={{ color: "var(--color-dashboard-accent-display)" }}>
							{t("realtor.referrals.title")}
						</span>
					</h1>
					<p
						className="text-xs mt-1"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.referrals.subtitle")}
					</p>
				</div>
				<StudioButton
					variant="secondary"
					size="sm"
					className="gap-1.5"
					disabled={exporting}
					onClick={handleExport}
				>
					{exporting ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Download className="h-4 w-4" />
					)}
					{t("realtor.referrals.exportCta")}
				</StudioButton>
			</header>

			{/* Credit-pending banner — V1 doesn't compute credit yet. */}
			<Card
				className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]"
				style={{
					borderColor: "var(--color-dashboard-pill-awaiting)",
				}}
			>
				<CardContent className="p-3 flex items-start gap-2">
					<Sparkles
						className="h-3.5 w-3.5 shrink-0 mt-0.5"
						style={{ color: "var(--color-dashboard-pill-awaiting)" }}
					/>
					<p
						className="text-[11px] leading-relaxed"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{t("realtor.referrals.creditPendingBanner")}
					</p>
				</CardContent>
			</Card>

			{rows.length === 0 ? (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-3">
						<HandCoins
							className="h-5 w-5 mx-auto"
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						/>
						<div className="space-y-1">
							<h2
								className="text-sm font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.referrals.emptyTitle")}
							</h2>
							<p
								className="text-xs max-w-sm mx-auto"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.referrals.emptyBody")}
							</p>
						</div>
						<StudioButton
							variant="secondary"
							size="sm"
							onClick={() => navigate("/realtor/listings")}
						>
							{t("realtor.referrals.gotoListings")}
						</StudioButton>
					</CardContent>
				</Card>
			) : (
				<ul className="space-y-2">
					{rows.map((row) => (
						<ReferralRowCard key={row.referral.id} row={row} />
					))}
				</ul>
			)}
		</div>
	);
}

function ReferralRowCard({ row }: { row: ReferralRow }) {
	const { t } = useTranslation();
	const { referral, listing, handoff } = row;
	return (
		<li
			className="rounded-md border p-3 space-y-1.5"
			style={{
				backgroundColor: "var(--color-dashboard-bg)",
				borderColor: "var(--color-dashboard-border)",
			}}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p
						className="text-sm font-medium truncate"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{listing?.headline ?? t("realtor.referrals.listingMissing")}
					</p>
					{listing && (
						<p
							className="text-[11px] truncate"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{listing.locality}, {listing.city}
						</p>
					)}
				</div>
				<StatusPill status={referral.status} />
			</div>
			<div
				className="flex items-center justify-between text-[11px]"
				style={{ color: "var(--color-dashboard-muted)" }}
			>
				<span>
					{t("realtor.referrals.onboardedOn", {
						date: handoff?.onboardedAt
							? new Date(handoff.onboardedAt).toLocaleDateString()
							: new Date(referral.createdAt).toLocaleDateString(),
					})}
				</span>
				<span className="italic">{t("realtor.referrals.creditPending")}</span>
			</div>
		</li>
	);
}

function StatusPill({ status }: { status: ReferralStatus }) {
	const { t } = useTranslation();
	const config = STATUS_CONFIG[status];
	const Icon = STATUS_ICONS[status];
	return (
		<span
			className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
			style={{
				backgroundColor: config.bg,
				color: config.text,
			}}
		>
			<Icon className="h-3 w-3" />
			{t(`realtor.referrals.status.${status}`)}
		</span>
	);
}

const STATUS_CONFIG: Record<ReferralStatus, { bg: string; text: string }> = {
	pending: {
		bg: "var(--color-dashboard-pill-info-bg)",
		text: "var(--color-dashboard-pill-info)",
	},
	converted: {
		bg: "var(--color-dashboard-pill-awaiting-bg)",
		text: "var(--color-dashboard-pill-awaiting)",
	},
	hired_designer: {
		bg: "var(--color-dashboard-pill-done-bg)",
		text: "var(--color-dashboard-pill-done)",
	},
	hired_contractor: {
		bg: "var(--color-dashboard-pill-done-bg)",
		text: "var(--color-dashboard-pill-done)",
	},
};

const STATUS_ICONS: Record<
	ReferralStatus,
	React.ComponentType<{ className?: string }>
> = {
	pending: Clock,
	converted: Sparkles,
	hired_designer: CheckCircle2,
	hired_contractor: CheckCircle2,
};
